<template>
  <div class="page">
    <HeaderInterview></HeaderInterview>
    <div class="body">
      <!-- 错误页面 -->
      <div v-if="hasError" class="error-page">
        <div class="error-icon">
          <van-icon name="warning-o" size="64" color="#F25058" />
        </div>
        <div class="error-message">{{errorMsg}}</div>
      </div>

      <template v-else>
        <!-- 标题 -->
        <div class="page-title">预约面试时间</div>

        <!-- 问候语 -->
        <div class="greeting-section">
          <div class="greeting">{{info.name || '--'}}，您好：</div>
          <div class="notice">
            感谢您投递汽车之家【<span class="position-name">{{info.positionName || '--'}}</span>】职位，恭喜您通过简历筛选，进入到了面试阶段，请在下面日历中选择您方便面试的时间
          </div>
        </div>

        <!-- 时间选择 -->
        <div v-if="!info.selected" class="time-section">
          <div class="section-label">*请选择面试时间：</div>
          <div v-if="loading" class="loading-wrap">
            <van-loading type="spinner" color="#206CFE" />
          </div>
          <template v-else-if="info.list && info.list.length > 0">
            <van-radio-group v-model="selectedId">
              <div
                class="time-item"
                v-for="item in info.list"
                :key="item.id"
                :class="{selected: selectedId === item.id}"
                @click="handleSelect(item)">
                <van-radio :name="'preset_' + item.id" :disabled="info.selected" checked-color="#206CFE">
                  <div class="time-text">
                    {{formatDate(item.interviewStartTime)}} {{formatTimeRange(item.interviewStartTime, item.interviewEndTime)}}
                  </div>
                </van-radio>
              </div>
              <!-- 自定义时间选项 -->
              <div
                class="time-item custom-option"
                :class="{selected: selectedId === 'custom'}"
                @click="handleSelectCustom">
                <van-radio name="custom" :disabled="info.selected" checked-color="#206CFE">
                  <div class="time-text">以上时间均不合适，我要自定义面试时间</div>
                </van-radio>
              </div>
            </van-radio-group>

            <!-- 自定义日历 -->
            <div v-if="showCustomCalendar" class="custom-calendar">
              <div class="calendar-title">请选择面试时间（至少选择2个时间段，连续一小时算一个时间段）</div>
              <div class="calendar-scroll-wrapper">
                <table class="calendar-grid">
                  <thead>
                    <tr class="calendar-header-row">
                      <th class="time-label-col"></th>
                      <th v-for="day in customWorkdays" :key="day.date" class="calendar-header-cell">
                        {{day.weekDay}}<br/>{{day.dateStr}}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="timeSlot in timeSlots" :key="timeSlot.time">
                      <td class="time-label">{{timeSlot.time}}</td>
                      <td
                        v-for="day in customWorkdays"
                        :key="day.date + '_' + timeSlot.time"
                        class="calendar-cell"
                        :class="getCellClass(day.date, timeSlot.time)"
                        @click="handleCellClick(day.date, timeSlot.time)">
                      </td>
                    </tr>
                    <!-- 结束时间边界 20:00 -->
                    <tr class="end-time-row">
                      <td class="time-label end-label">20:00</td>
                      <td v-for="day in customWorkdays" :key="day.date + '_end'" class="end-cell"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </template>
          <div v-else class="no-data">暂无可选面试时间</div>
        </div>
        <!-- 已选择/已放弃提示 -->
        <div v-if="info.selected" class="time-section">
          <template v-if="info.selectedTime">
            <div class="section-label">您已选择面试时间：</div>
            <div class="time-item selected">
              <div class="time-text">{{formatDate(info.selectedTime.interviewStartTime)}} {{formatTimeRange(info.selectedTime.interviewStartTime, info.selectedTime.interviewEndTime)}}</div>
            </div>
          </template>
          <template v-else-if="info.customList && info.customList.length > 0">
            <div class="section-label">您已选择自定义面试时间：</div>
            <div v-for="(item, index) in info.customList" :key="index" class="time-item selected">
              <div class="time-text">{{formatDate(item.interviewStartTime)}} {{formatTimeRange(item.interviewStartTime, item.interviewEndTime)}}</div>
            </div>
          </template>
          <template v-else>
            <div class="section-label">您已放弃参加面试</div>
          </template>
        </div>

        <!-- 注意事项 -->
        <div class="tips-section">
          <div class="tips-title">注意事项：</div>
          <div class="tips-content">1、您选择时间后，我们会给你发送面试邀请短信及邮件，短信邮件中会有注意事项，请您注意查收</div>
          <div v-if="info.telNum" class="tips-content">2、若以上面试时间均不合适，请联系您的招聘经理：{{info.telNum}}</div>
        </div>
        <!-- 按钮区 -->
        <div v-if="!info.selected" class="btn-section">
          <van-button class="btn-cancel" @click="notAttend">不参加面试</van-button>
          <van-button class="btn-submit" :disabled="!canSubmit" @click="submit">已选好时间，提交</van-button>
        </div>

        <!-- 职位基本信息 -->
        <div class="position-info-section">
          <div class="section-title">职位基本信息</div>
          <div class="info-item">
            <div class="info-label">职位名称：</div>
            <div class="info-value">{{info.positionName || '--'}}</div>
          </div>
          <div class="info-item">
            <div class="info-label">职位职责：</div>
            <div class="info-value">{{info.positionDuty || '--'}}</div>
          </div>
          <div class="info-item">
            <div class="info-label">岗位要求：</div>
            <div class="info-value">{{info.positionRequire || '--'}}</div>
          </div>
        </div>
      </template>
    </div>
    <Footer></Footer>
  </div>
</template>

<script>
import Vue from "vue";
import { Toast, RadioGroup, Radio, Button, Loading, Icon, Dialog } from 'vant';
import 'vant/lib/index.css';
import HeaderInterview from "./HeaderInterview";
import Footer from "./Footer";
import Request from "../utils/Request";
import Urls from "../utils/Urls";
import Utils from "../utils/Utils";

Vue.use(RadioGroup);
Vue.use(Radio);
Vue.use(Button);
Vue.use(Loading);
Vue.use(Icon);

export default {
  name: "InterviewBooking",
  components: {
    HeaderInterview,
    Footer
  },
  data() {
    return {
      app: "",
      sign: "",
      loading: false,
      submitting: false,
      hasError: false,
      errorMsg: "",
      info: {
        name: "",
        positionName: "",
        positionDuty: "",
        positionRequire: "",
        selected: false,
        list: [],
        customList: [],
        telNum: ""
      },
      selectedId: null,
      showCustomCalendar: false,
      customWorkdays: [],
      timeSlots: [],
      selectedCells: []
    };
  },
  mounted() {
    this.app = Utils.getApp();
    this.sign = this.$route.query.sign || "";

    if (!this.sign) {
      this.hasError = true;
      this.errorMsg = "缺少必要参数，请检查链接是否完整";
      return;
    }

    this.getInterviewInfo();
  },
  methods: {
    // 获取面试信息
    getInterviewInfo() {
      this.loading = true;
      Toast.loading({
        duration: 0,
        forbidClick: true,
        message: '加载中...'
      });

      Request.requestJson(Urls.getInterviewInfo, { sign: this.sign })
        .then(data => {
          this.loading = false;
          Toast.clear();

          if (data) {
            this.info = data;
            if (data.selected && data.list) {
              data.list.forEach(item => {
                if (item.selected) {
                  this.selectedId = 'preset_' + item.id;
                }
              });
            }
            if (data.customList && data.customList.length > 0) {
              this.selectedId = 'custom';
              this.initCustomCalendar();
              this.selectedCells = data.customList.map(item => ({
                date: item.interviewStartTime.substring(0, 10),
                time: item.interviewStartTime.substring(11, 16)
              }));
            }
          } else {
            this.hasError = true;
            this.errorMsg = "暂无面试信息";
          }
        })
        .catch((err) => {
          this.loading = false;
          Toast.clear();
          this.hasError = true;
          this.errorMsg = err && err.info ? err.info : '获取面试信息失败';
        });
    },

    // 选择预设时间
    handleSelect(item) {
      if (this.info.selected) {
        return;
      }
      this.selectedId = 'preset_' + item.id;
      this.showCustomCalendar = false;
      this.selectedCells = [];
    },

    // 选择自定义时间
    handleSelectCustom() {
      if (this.info.selected) {
        return;
      }
      this.selectedId = 'custom';
      this.initCustomCalendar();
      this.showCustomCalendar = true;
    },

    // 不参加面试
    notAttend() {
      Dialog.confirm({
        title: '提示',
        message: '您确认不参加汽车之家此面试吗？',
        confirmButtonText: '不参加',
        cancelButtonText: '再考虑一下',
        confirmButtonColor: '#F25058'
      }).then(() => {
        this.doSubmit(0, null);
      }).catch(() => {
        // 取消
      });
    },

    // 提交
    submit() {
      if (!this.selectedId) {
        Toast('请选择面试时间');
        return;
      }

      if (this.selectedId === 'custom') {
        // 校验：连续两个单元格（1小时）算一个时间段，至少需要2个时间段
        const validSlotCount = this.countValidTimeSlots();
        if (validSlotCount < 2) {
          Toast('至少选择2个时间段，连续一小时算一个时间段');
          return;
        }
      }

      Dialog.confirm({
        title: '提示',
        message: '您确认提交所选面试时间吗？',
        confirmButtonText: '确认提交',
        cancelButtonText: '再考虑一下'
      }).then(() => {
        if (this.selectedId === 'custom') {
          this.doSubmitCustom();
        } else {
          const interviewTimeId = this.selectedId.replace('preset_', '');
          this.doSubmit(1, interviewTimeId);
        }
      }).catch(() => {
        // 取消
      });
    },

    // 执行提交预设时间
    doSubmit(attendInterview, interviewTimeId) {
      this.submitting = true;
      Toast.loading({
        duration: 0,
        forbidClick: true,
        message: '提交中...'
      });

      var params = {
        sign: this.sign,
        attendInterview: attendInterview,
        interviewTimeId: interviewTimeId
      };

      Request.requestJson(Urls.saveInterviewTime, params)
        .then(() => {
          this.submitting = false;
          Toast.clear();
          Toast.success('提交成功');

          setTimeout(() => {
            this.getInterviewInfo();
          }, 1000);
        })
        .catch((err) => {
          this.submitting = false;
          Toast.clear();
          Toast(err && err.info ? err.info : '提交失败，请稍后重试');
        });
    },

    // 执行提交自定义时间（每个单元格单独一个元素）
    doSubmitCustom() {
      this.submitting = true;
      Toast.loading({
        duration: 0,
        forbidClick: true,
        message: '提交中...'
      });

      const customList = this.convertToDateList();

      var params = {
        sign: this.sign,
        attendInterview: 1,
        customList: customList
      };

      Request.requestJson(Urls.saveInterviewTime, params)
        .then(() => {
          this.submitting = false;
          Toast.clear();
          Toast.success('提交成功');

          setTimeout(() => {
            this.getInterviewInfo();
          }, 1000);
        })
        .catch((err) => {
          this.submitting = false;
          Toast.clear();
          Toast(err && err.info ? err.info : '提交失败，请稍后重试');
        });
    },

    // 格式化日期
    formatDate(dateTimeStr) {
      if (!dateTimeStr) return '--';
      const dateStr = dateTimeStr.substring(0, 10);
      const parts = dateStr.split('-');
      let date = parts[1] + '月' + parts[2] + '日';

      const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
      const d = new Date(dateStr.replace(/-/g, '/'));
      if (!isNaN(d.getTime())) {
        date += ' ' + weekDays[d.getDay()];
      }

      return date;
    },

    // 格式化时间段
    formatTimeRange(startTime, endTime) {
      if (!startTime || !endTime) return '--';
      const start = startTime.substring(11, 16);
      const end = endTime.substring(11, 16);
      return start + '-' + end;
    },

    // 初始化自定义日历
    initCustomCalendar() {
      this.customWorkdays = this.getNextWorkdays();
      this.timeSlots = this.generateTimeSlots();
    },

    // 获取从明天开始的5个工作日
    getNextWorkdays() {
      const workdays = [];
      const today = new Date();
      let daysToAdd = 1;

      while (workdays.length < 5) {
        const checkDate = new Date(today);
        checkDate.setDate(today.getDate() + daysToAdd);
        const dayOfWeek = checkDate.getDay();

        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
          const month = checkDate.getMonth() + 1;
          const date = checkDate.getDate();
          workdays.push({
            date: this.formatDateFull(checkDate),
            dateStr: month + '/' + date,
            weekDay: weekDays[dayOfWeek]
          });
        }
        daysToAdd++;
      }
      return workdays;
    },

    // 格式化完整日期 YYYY-MM-DD
    formatDateFull(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return year + '-' + month + '-' + day;
    },

    // 生成时间段列表 (09:00-19:30，每30分钟，共22个)
    generateTimeSlots() {
      const slots = [];
      for (let hour = 9; hour < 20; hour++) {
        for (let minute = 0; minute < 60; minute += 30) {
          slots.push({ time: this.padZero(hour) + ':' + this.padZero(minute) });
        }
      }
      return slots;  // 22个格子: 09:00, 09:30, 10:00, ... 19:30
    },

    // 补零
    padZero(num) {
      return num < 10 ? '0' + num : '' + num;
    },

    // 获取单元格样式
    getCellClass(date, time) {
      const isSelected = this.selectedCells.some(
        cell => cell.date === date && cell.time === time
      );
      const isInRange = this.isInRange(date, time);
      return {
        selected: isSelected,
        'in-range': isInRange
      };
    },

    // 判断是否在已选范围内（用于连续选择高亮）
    isInRange(date, time) {
      if (this.selectedCells.length === 0) return false;

      const selectedTimes = this.selectedCells
        .filter(cell => cell.date === date)
        .map(cell => cell.time)
        .sort();

      if (selectedTimes.length < 2) return false;

      const timeIndex = this.timeSlots.findIndex(slot => slot.time === time);
      const minIndex = this.timeSlots.findIndex(slot => slot.time === selectedTimes[0]);
      const maxIndex = this.timeSlots.findIndex(slot => slot.time === selectedTimes[selectedTimes.length - 1]);

      return timeIndex > minIndex && timeIndex < maxIndex;
    },

    // 点击单元格
    handleCellClick(date, time) {
      const index = this.selectedCells.findIndex(
        cell => cell.date === date && cell.time === time
      );

      if (index > -1) {
        this.selectedCells.splice(index, 1);
      } else {
        this.selectedCells.push({ date, time });
      }
    },

    // 将选中的时间转换为提交格式（每个单元格单独一个元素）
    convertToDateList() {
      const result = [];

      this.selectedCells.forEach(cell => {
        const dateStr = cell.date;
        const timeStr = cell.time;
        const parts = timeStr.split(':');
        const h = parseInt(parts[0]);
        const m = parseInt(parts[1]);

        // 计算结束时间（开始时间+30分钟）
        let endHour = h;
        let endMinute = m + 30;
        if (endMinute >= 60) {
          endHour += 1;
          endMinute -= 60;
        }

        result.push({
          interviewStartTime: dateStr + ' ' + timeStr + ':00',
          interviewEndTime: dateStr + ' ' + this.padZero(endHour) + ':' + this.padZero(endMinute) + ':00'
        });
      });

      return result;
    },

    // 统计有效时间段数量（连续两个单元格=1个时间段）
    countValidTimeSlots() {
      if (this.selectedCells.length === 0) return 0;

      // 按日期和时间排序
      const sortedCells = [...this.selectedCells].sort((a, b) => {
        const keyA = a.date + ' ' + a.time;
        const keyB = b.date + ' ' + b.time;
        return keyA.localeCompare(keyB);
      });

      let validSlotCount = 0;
      let consecutiveCount = 0;

      for (let i = 0; i < sortedCells.length; i++) {
        consecutiveCount++;

        // 检查是否与下一个连续
        if (i < sortedCells.length - 1) {
          const current = sortedCells[i];
          const next = sortedCells[i + 1];

          // 同一天且时间相差30分钟才算连续
          const isConsecutive = current.date === next.date &&
            this.isTimeConsecutive(current.time, next.time);

          if (!isConsecutive) {
            // 每2个连续单元格算1个时间段
            validSlotCount += Math.ceil(consecutiveCount / 2);
            consecutiveCount = 0;
          }
        }
      }
      // 结算最后一组
      validSlotCount += Math.ceil(consecutiveCount / 2);

      return validSlotCount;
    },

    // 判断两个时间点是否连续（相差30分钟）
    isTimeConsecutive(time1, time2) {
      const [h1, m1] = time1.split(':').map(Number);
      const [h2, m2] = time2.split(':').map(Number);

      const total1 = h1 * 60 + m1;
      const total2 = h2 * 60 + m2;

      return total2 - total1 === 30;
    }
  },
  computed: {
    canSubmit() {
      if (!this.selectedId) return false;
      if (this.selectedId === 'custom') {
        return this.countValidTimeSlots() >= 2;
      }
      return true;
    }
  }
};
</script>

<style scoped>
.page {
  background-color: #F5F7FA;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.body {
  flex: 1;
  padding: 16px;
  padding-bottom: 32px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.page-title {
  text-align: center;
  font-family: PingFangSC-Medium;
  font-size: 20px;
  color: #20253F;
  font-weight: 600;
  padding: 20px 0;
  border-bottom: 1px solid #E6EBF5;
  margin-bottom: 20px;
}

.greeting-section {
  margin-bottom: 20px;
}

.greeting-section .greeting {
  font-family: PingFangSC-Medium;
  font-size: 16px;
  color: #20253F;
  margin-bottom: 12px;
}

.greeting-section .notice {
  font-family: PingFangSC-Regular;
  font-size: 13px;
  color: #666D7F;
  line-height: 22px;
}

.greeting-section .notice .position-name {
  color: #206CFE;
  font-weight: 500;
}

.time-section {
  background: #FFFFFF;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.time-section .section-label {
  font-family: PingFangSC-Medium;
  font-size: 13px;
  color: #20253F;
  margin-bottom: 14px;
}

.time-section .section-label::before {
  content: '*';
  color: #F25058;
  margin-right: 4px;
}

.loading-wrap {
  display: flex;
  justify-content: center;
  padding: 30px 0;
}

.no-data {
  text-align: center;
  padding: 30px 0;
  color: #9499B0;
  font-size: 13px;
}

.time-item {
  padding: 12px 14px;
  margin-bottom: 10px;
  background: #F8F9FC;
  border-radius: 6px;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.time-item.selected {
  background: linear-gradient(270deg, #206CFE 0%, #4C88FF 100%);
  border-color: #206CFE;
}

.time-item.selected .time-text {
  color: #FFFFFF;
}

.time-text {
  font-family: PingFangSC-Regular;
  font-size: 13px;
  color: #20253F;
}

.time-item >>> .van-radio {
  width: 100%;
}

.time-item >>> .van-radio__label {
  color: #20253F;
}

.time-item.selected >>> .van-radio__label {
  color: #FFFFFF;
}

.time-item >>> .van-radio__icon {
  font-size: 18px;
}

.time-item >>> .van-radio__icon .van-icon {
  border-color: #D4D8E0;
}

.time-item.selected >>> .van-radio__icon .van-icon {
  background-color: #FFFFFF;
  border-color: #FFFFFF;
}

.tips-section {
  background: #FFF9E6;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.tips-section .tips-title {
  font-family: PingFangSC-Medium;
  font-size: 13px;
  color: #FF8C3B;
  margin-bottom: 8px;
}

.tips-section .tips-content {
  font-family: PingFangSC-Regular;
  font-size: 12px;
  color: #666D7F;
  line-height: 20px;
}

.btn-section {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 24px;
}

.btn-section .van-button {
  flex: 1;
  border-radius: 6px;
  font-family: PingFangSC-Medium;
  font-size: 14px;
}

.btn-section .btn-cancel {
  background: #206CFE;
  border: none;
  color: #FFFFFF;
}

.btn-section .btn-submit {
  background: linear-gradient(90deg, #FF8C3B 0%, #FF6600 100%);
  border: none;
  color: #FFFFFF;
}

.btn-section .van-button--disabled {
  background: #D4D8E0;
  opacity: 1;
}

.position-info-section {
  background: #FFFFFF;
  border-radius: 8px;
  padding: 16px;
}

.position-info-section .section-title {
  font-family: PingFangSC-Medium;
  font-size: 15px;
  color: #20253F;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #E6EBF5;
}

.position-info-section .info-item {
  margin-bottom: 14px;
}

.position-info-section .info-item:last-child {
  margin-bottom: 0;
}

.position-info-section .info-label {
  font-family: PingFangSC-Medium;
  font-size: 13px;
  color: #20253F;
  margin-bottom: 6px;
}

.position-info-section .info-value {
  font-family: PingFangSC-Regular;
  font-size: 12px;
  color: #666D7F;
  line-height: 20px;
  white-space: pre-wrap;
}

.error-page {
  text-align: center;
  padding: 60px 20px;
  background: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0px 2px 10px 0px rgba(134, 135, 152, 0.09);
}

.error-page .error-icon {
  margin-bottom: 20px;
}

.error-page .error-message {
  font-family: PingFangSC-Regular;
  font-size: 14px;
  color: #666D7F;
  line-height: 22px;
}

.custom-option {
  margin-top: 10px;
  border: 2px dashed #D4D8E0;
  background: #FAFBFC;
}

.custom-option.selected {
  border-style: solid;
}

.custom-calendar {
  margin-top: 16px;
  padding: 12px;
  background: #FAFBFC;
  border-radius: 8px;
  border: 1px solid #E6EBF5;
}

.calendar-title {
  font-family: PingFangSC-Regular;
  font-size: 12px;
  color: #666D7F;
  margin-bottom: 12px;
  text-align: center;
}

.calendar-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.calendar-grid {
  border-collapse: collapse;
  min-width: 100%;
}

.calendar-header-row th {
  padding: 4px 2px;
  font-family: PingFangSC-Regular;
  font-size: 10px;
  color: #666D7F;
  text-align: center;
  white-space: nowrap;
  border: none;
}

.time-label-col {
  width: 40px;
  min-width: 40px;
  border: none;
  vertical-align: top;
}

.time-label {
  position: relative;
  top: -7px;
  font-family: PingFangSC-Regular;
  font-size: 10px;
  color: #20253F;
  text-align: right;
  padding: 0 4px 0 0;
  border: none;
  height: 0;
  vertical-align: top;
}

.calendar-cell {
  width: 48px;
  height: 28px;
  border: 1px solid #E6EBF5;
  border-left: none;
  background: #FFFFFF;
  cursor: pointer;
}

.calendar-cell:hover {
  background: #EEF3FF !important;
}

.calendar-cell.selected {
  background: #55aaff !important;
}

.calendar-cell.in-range {
  background: #D4E2FF;
}

.calendar-cell:last-child {
  border-right: 1px solid #E6EBF5;
}

.end-time-row {
  height: 0;
}

.end-label {
  position: relative;
  top: -7px;
}

.end-cell {
  height: 0;
  border: none;
  border-top: 1px solid #E6EBF5;
}
</style>
